// Catalog.ID Client — Claim sharing (the symkey re-wrap step)
// ============================================================================
// Sharing a claim is always the same three moves: fetch the recipient's
// `attr_encrypt` public key, peel this account's own wrap off the claim's
// symmetric key (walking the rotation chain when the self wrap carries a
// keyIndex), and re-wrap that symmetric key to the recipient before POSTing it.
//
// It lived only inside `pane-types/sharing-pane.ts` until the chat contact flow
// needed the identical sequence from a key-grant handler
// (`key-grant/chat-address-handlers.ts` — sharing a `chat_address` claim IS the
// consent record, contact-flow.md §8). Two copies of a key-chain walk is one
// copy too many: the `state/org-keys.ts` header records what happened the last
// time three panes each grew their own — the copies disagreed about key
// ordering, and the disagreement was invisible until a rotation.
//
// Placed beside `org-keys.ts` for the same reason it is: this is a cross-layer
// helper (api + crypto + keybag state) that neither the pure-transport `api/`
// nor the pure-crypto `crypto/` layer may own.

import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import type { ClaimDetail, ClaimKeyEntry } from '../api/types';
import { reEncryptClaimKey, reEncryptClaimKeyWithChain } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from './keys';

/** The keybag slot every claim symmetric key is wrapped to. */
export const ATTR_ENCRYPT_KEY_ID = 'attr_encrypt';

/**
 * A named refusal from the share sequence. `code` is the stable machine-readable
 * form (key-grant handlers branch on it); `message` stays the human sentence the
 * sharing pane has always shown.
 */
export class ClaimShareError extends Error {
    constructor(readonly code: string, message: string) {
        super(message);
        this.name = 'ClaimShareError';
    }
}

/** The claim's own `self` wrap — the only key row this account can peel. */
export function findSelfKey(detail: ClaimDetail): ClaimKeyEntry | null {
    return detail.keys.find(k => k.recipientType === 'self') ?? null;
}

/**
 * Whether this claim is already shared with `recipientCid`.
 *
 * Only true `share` rows count. A `validator` row and the owner's own `self`
 * wrap live in the same table but are not shares, and treating either as one
 * would make a re-share look like a no-op.
 */
export function isSharedWith(detail: ClaimDetail, recipientCid: string): boolean {
    return detail.keys.some(k => k.recipientType === 'share' && k.recipientCid === recipientCid);
}

/**
 * Re-wrap a claim's symmetric key from this account's self wrap to
 * `recipientPublicKey`. Chain-aware: a self wrap with `keyIndex > 0` was
 * produced by a key rotation and has to be peeled layer by layer, or every
 * post-rotation share fails.
 */
export async function rewrapClaimKeyFor(
    detail: ClaimDetail, recipientPublicKey: Uint8Array,
): Promise<string> {
    const selfKey = findSelfKey(detail);
    if (!selfKey) throw new ClaimShareError('claim-has-no-self-key', 'No self key found');

    const mySecretKey = keys.getPrivateKey(ATTR_ENCRYPT_KEY_ID);
    if (!mySecretKey) {
        throw new ClaimShareError('attr-encrypt-not-in-bag', 'Private key not available');
    }

    if (selfKey.keyIndex > 0) {
        return await reEncryptClaimKeyWithChain(
            selfKey.encryptedKey, keys.getKeyChain(ATTR_ENCRYPT_KEY_ID),
            selfKey.keyIndex, recipientPublicKey);
    }
    return await reEncryptClaimKey(
        selfKey.encryptedKey, aes.fromBase64(mySecretKey), recipientPublicKey);
}

/** The recipient's `attr_encrypt` public key bytes. */
export async function resolveRecipientAttrEncrypt(recipientCid: string): Promise<Uint8Array> {
    const recipientKeys = await accountApi.getPublicKeys(recipientCid);
    const recipientAttr = recipientKeys.find(k => k.keyId === ATTR_ENCRYPT_KEY_ID);
    if (!recipientAttr) {
        throw new ClaimShareError(
            'recipient-has-no-attr-encrypt', 'Recipient has no attr_encrypt key');
    }
    return aes.fromBase64(recipientAttr.publicKey);
}

/**
 * Share `claimUuid` with `recipientCid`, idempotently.
 *
 * Returns `'already-shared'` without touching the network beyond the read when
 * a `share` row for that recipient already exists. Idempotency is not a
 * nicety here: the contact flow retries the share on every connected transition
 * (pane-protocol.md §4.5), so a second call must be a success, not a duplicate
 * write or a 400.
 *
 * `detail` may be passed by a caller that already fetched it, to avoid a second
 * `GET /api/claims/{uuid}`.
 */
export async function shareClaimWithRecipient(
    claimUuid: string, recipientCid: string, detail?: ClaimDetail,
): Promise<'shared' | 'already-shared'> {
    const claim = detail ?? await claimsApi.getClaim(claimUuid);
    if (isSharedWith(claim, recipientCid)) return 'already-shared';

    const recipientPublicKey = await resolveRecipientAttrEncrypt(recipientCid);
    const encryptedKey = await rewrapClaimKeyFor(claim, recipientPublicKey);
    await claimsApi.shareClaim(claimUuid, { recipientCid, encryptedKey });
    return 'shared';
}
